import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge } from 'rxjs';

export interface AudioState {
  playing: boolean;
  duration?: number;
  currentTime?: number;
  canplay: boolean;
  error: boolean;
}

export interface AudioTrack {
  id: string;
  src: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class AudioPlayerService {
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _state = signal<AudioState>({
    playing: false,
    duration: undefined,
    currentTime: undefined,
    canplay: false,
    error: false,
  });

  private readonly _currentTrack = signal<AudioTrack | null>(null);

  private readonly _audio = new Audio();

  private readonly _audioEvents: readonly (keyof HTMLElementEventMap)[] = [
    'ended',
    'error',
    'play',
    'playing',
    'pause',
    'timeupdate',
    'canplay',
    'loadedmetadata',
    'loadstart',
  ];

  private readonly _eventMap = this._audioEvents.map((event) =>
    fromEvent(this._audio, event)
  );

  public readonly currentTrack = this._currentTrack.asReadonly();

  get state() {
    return this._state.asReadonly();
  }

  constructor() {
    merge(...this._eventMap)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (event) => {
          switch (event.type) {
            case 'canplay':
              this._updateState({
                duration: this._audio.duration,
                canplay: true,
              });
              break;

            case 'playing':
              this._updateState({
                playing: true,
              });
              break;

            case 'pause':
              this._updateState({
                playing: false,
              });
              break;

            case 'timeupdate':
              this._updateState({
                currentTime: this._audio.currentTime,
              });
              break;

            case 'ended':
            case 'error':
              this._resetState();
              break;
          }
        },
      });
  }

  load(track: AudioTrack) {
    this._audio.src = track.src;
    this._currentTrack.set(track);

    this._audio.load();
  }

  play() {
    this._audio.play();
  }

  stop() {
    this._currentTrack.set(null);
    this._audio.pause();
    this.seek(0);
  }

  seek(seconds: number) {
    this._audio.currentTime = seconds;
  }

  private _updateState(newState: Partial<AudioState>) {
    this._state.update((currentState) => ({ ...currentState, ...newState }));
  }

  private _resetState() {
    this._state.set({
      playing: false,
      duration: undefined,
      currentTime: undefined,
      canplay: false,
      error: false,
    });
  }
}

// @Injectable({
//   providedIn: 'root',
// })
// export class AudioPlayerService {
//   private _audio: HTMLAudioElement | null = null;
//   private _currentAudioSource = signal<string | null>(null);

//   currentAudioSource = this._currentAudioSource.asReadonly();

//   play(url: string) {
//     this.stop();

//     this._audio = new Audio(url);
//     this._audio.onended = () => {
//       this.stop();
//     };

//     this._audio.play();
//     this._currentAudioSource.set(url);
//   }

//   stop() {
//     if (!this._audio) {
//       return;
//     }

//     this._audio.pause();
//     this._audio.currentTime = 0;
//     this._audio = null;
//     this._currentAudioSource.set(null);
//   }
// }
